/* @flow */

import classNames from 'classnames';
import * as React from 'react';
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';

import styles from './TakeOver.style';
import {
  enableBodyScrolling,
  disableBodyScrolling,
} from 'src/shared/core/util/browserUtil';
import CloseButton from 'src/shared/ui/component/button/CloseButton';
import useTrapTabs from 'src/shared/app/base/hook/useTrapTabs';
import Container from 'src/shared/app/base/component/container/Container';

type Props = {
  bypassOpeningAnimation?: boolean,
  hideCloseButton?: boolean,
  forceClosing?: boolean,
  handleOpen?: () => void,
  handleDismiss?: () => void,
  children: React.Node,
};

const useStyles = createUseThemeStyles(styles);

const TakeOver = ({
  bypassOpeningAnimation,
  hideCloseButton,
  forceClosing,
  children,
  handleOpen,
  handleDismiss,
}: Props): React.Node => {
  /*
   |----------------------------------------------------------------------------
   | State
   |----------------------------------------------------------------------------
   */

  const [isHidden, setIsHidden] = React.useState(!bypassOpeningAnimation);

  /*
   |----------------------------------------------------------------------------
   | Effects
   |----------------------------------------------------------------------------
   */

  React.useEffect(() => {
    if (!bypassOpeningAnimation) {
      open();
    }

    disableBodyScrolling();

    return () => {
      enableBodyScrolling();
    };
  }, []);

  React.useEffect(() => {
    if (forceClosing) {
      close();
    }
  }, [forceClosing]);

  /*
   |----------------------------------------------------------------------------
   | Event Handlers
   |----------------------------------------------------------------------------
   */

  const onDismiss = () => close();

  const onTransitionEnd = ({ target }: TransitionEvent) => {
    const { current: rootElt } = rootRef;

    if (target === rootElt) {
      if (isHidden) {
        if (handleDismiss) {
          handleDismiss();
        }
      } else if (handleOpen) {
        handleOpen();
      }
    }
  };

  /*
   |----------------------------------------------------------------------------
   | Functions
   |----------------------------------------------------------------------------
   */

  const open = () => {
    window.requestAnimationFrame(() =>
      window.requestAnimationFrame(() => setIsHidden(false)),
    );
  };

  const close = () => {
    window.requestAnimationFrame(() =>
      window.requestAnimationFrame(() => setIsHidden(true)),
    );
  };

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes = useStyles();

  const rootClasses = classNames(classes.root, {
    [classes['root-isHidden']]: isHidden,
  });

  /*
   |----------------------------------------------------------------------------
   | Refs
   |----------------------------------------------------------------------------
   */

  const rootRef = React.createRef();

  useTrapTabs(rootRef);

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const closeBtn = !hideCloseButton && (
    <CloseButton circled layoutClass={classes.closeBtn} onClick={onDismiss} />
  );

  return (
    <div
      className={rootClasses}
      onTransitionEnd={onTransitionEnd}
      ref={rootRef}
    >
      <Container>
        <div className={classes.inner}>
          {closeBtn}
          {children}
        </div>
      </Container>
    </div>
  );
};

export default React.memo<Props>(TakeOver);
